import { useQuery } from 'react-query'
import { useContext } from 'preact/hooks'
import { UserContext } from '@/core/utils/user-context'
import { useStore } from '@/core'
import { fetch } from '@/core/utils/drf/fetch'
import {
  useAdminNonprofits,
  useAdminMicrosoftTeamsCompanies,
  useAdminOpportunities,
  useAdminCompanies,
  useAdminRSVPNonprofits,
  useAdminRSVPNonprofitContacts,
  useAdminSlackChannelSubscriptions,
  usePurposelyAdminOpportunityRequest,
  useDepartments,
  useCauses,
  useCompanyAdminEmployeeLocations,
  useMySlackChannels,
  useCompanyAdminEmployees,
  useRsvpEventLocations,
  useEmployeeRSVPNonprofits,
  usePublicNonprofits,
  usePublicNonprofitsSearch,
  usePublicNonprofitsSearchDetail,
  usePublicNonprofitsRegistrySearch,
} from '@u/drf/hooks'

import { causeTranslator } from '@/utils/i18n/translations/utility-functions'
import { useTranslation } from 'react-i18next'

export const types = {
  nonprofitSearch: () => {
    return {
      placeholder: 'Search Nonprofits (Every.org)',
      getResource: (slug) =>
        fetch('publicNonprofitsSearchDetail', { args: { identifier: slug } }),
      useResources: ({ search }) =>
        usePublicNonprofitsSearch({ queries: { q: search } }),
      useResource: (slug) => usePublicNonprofitsSearchDetail({ args: { identifier: slug } }),
      extractValueAndName: (r) => {
        // Every.org location is a plain string e.g. "City, State, ZIP Country"
        // or abbreviated e.g. "CAMBRIDGE, MA" — street addresses are never included
        const locStr = typeof r.location === 'string' ? r.location : ''
        const locParts = locStr.split(', ')
        const city = locParts[0] || ''
        const state = locParts[1] || ''
        const zipMatch = locStr.match(/\b(\d{5})\b/)
        const zip = zipMatch ? zipMatch[1] : ''
        return {
          value: r.slug,
          name: r.name,
          fields: {
            website: r.websiteUrl,
            description: r.description,
            ein: r.ein,
            city,
            state,
            zip,
          },
        }
      },
    }
  },
  nonprofitRegistrySearch: () => {
    return {
      placeholder: 'Search Canadian Nonprofits...',
      getResource: (bn) => fetch('publicNonprofitsRegistrySearch', { queries: { q: bn } }),
      useResources: ({ search }) =>
        usePublicNonprofitsRegistrySearch({ queries: { q: search } }),
      extractValueAndName: (r) => ({
        value: r.bn,
        name: `${r.legalName}${r.city ? ` (${r.city}, ${r.province})` : ''}`,
        fields: {
          website: r.website,
          city: r.city,
          province: r.province,
          address: r.addressLine1,
          postalCode: r.postalCode,
        },
      }),
    }
  },
  adminNonprofit: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.adminNonprofit'),
      getResource: (guid) =>
        fetch('adminNonprofit', { args: { guid }, token: user?.token }),
      useResources: ({ search }) => useAdminNonprofits({ queries: { search } }),
      extractValueAndName: (r) => ({
        value: r.guid,
        name: r.name || `<no name> - ${r.guid}`,
      }),
    }
  },
  nonprofitSignupSearch: () => {
    return {
      placeholder: "Search for your nonprofit...",
      getResource: (guid) =>
        fetch('publicNonprofits', { queries: { guid } }),
      useResources: ({ search }) => usePublicNonprofits({ queries: { search } }),
      extractValueAndName: (r) => ({
        value: r.guid,
        name: r.name,
      }),
    }
  },
  adminMicrosoftTeamsCompany: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: 'Microsoft Teams Company',
      getResource: (id) =>
        fetch('adminMicrosoftTeamsCompany', {
          args: { id },
          token: user?.token,
        }),
      useResources: ({ search }) =>
        useAdminMicrosoftTeamsCompanies({ queries: { search } }),
      extractValueAndName: (r) => ({
        value: r.id,
        name:
          `${r.companyName} - ${r.subscriptions.length} active channels` ||
          `<no name> - ${r.guid}`,
      }),
    }
  },
  adminOpportunity: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.adminOpportunity'),
      getResource: (guid) =>
        fetch('adminOpportunity', { args: { guid }, token: user?.token }),
      useResources: ({ search }) =>
        useAdminOpportunities({ queries: { search } }),
      extractValueAndName: (r) => ({
        value: r.guid,
        name: `${r.name} - ${r.nonprofit?.name || '(No Nonprofit Name)'}`,
      }),
    }
  },
  adminCompany: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.adminCompany'),
      getResource: (guid) => fetch('adminCompany', { args: { guid }, token: user?.token }),
      useResources: ({ search }) => useAdminCompanies({ queries: { search } }),
      extractValueAndName: (r) => {
        return { value: r.id, name: r.name }
      },
    }
  },
  adminRsvpNonprofit: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.adminRsvpNonprofit'),
      getResource: (nonprofitId) =>
        fetch('adminRsvpNonprofit', {
          args: { nonprofitId },
          token: user?.token,
        }),
      useResources: ({ search }) =>
        useAdminRSVPNonprofits({ queries: { search } }),
      extractValueAndName: (r) => {
        return { value: r.id, name: r.name }
      },
    }
  },
  adminRsvpNonprofitContact: () => {
    const user = useContext(UserContext)
    return {
      placeholder: 'Select contact',
      getResource: (contactId) =>
        fetch('adminRsvpNonprofitContact', {
          args: { contactId },
          token: user?.token,
        }),
      useResources: ({ search }) =>
        useAdminRSVPNonprofitContacts({ queries: { search } }),
      extractValueAndName: (r) => ({
        value: r.id,
        name: `${r.email} - ${r.firstName} ${r.lastName} - ${r.nonprofitName}`,
      }),
    }
  },
  adminSlackChannelSubscription: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.adminSlackChannelSubscription'),
      getResource: (guid) =>
        fetch('adminSlackChannelSubscriptions', {
          args: { guid },
          token: user?.token,
        }),
      useResources: ({ search }) =>
        useAdminSlackChannelSubscriptions({ queries: { search } }),
      extractValueAndName: (c) => ({
        value: c.id,
        name: `${c.companyName} - ${c.channelName}`,
      }),
    }
  },
  purposelyAdminOpportunityRequest: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.purposelyAdminOpportunityRequest'),
      getResource: (id) =>
        fetch('purposelyAdminOpportunityRequestDetail', {
          args: { id },
          token: user?.token,
        }),
      useResources: ({ search }) =>
        usePurposelyAdminOpportunityRequest({ queries: { search } }),
      extractValueAndName: (r) => {
        return {
          value: r.id,
          name: `${r.id} - ${r.companyName}`,
        }
      },
    }
  },
  departments: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.departments'),
      getResource: () =>
        fetch('departments', {
          token: user?.token,
        }),
      useResources: ({ search }) => useDepartments({ queries: { search } }),
      extractValueAndName: (r) => {
        return {
          value: r.id,
          name: r.name,
        }
      },
    }
  },
  causes: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.causes'),
      getResource: () =>
        fetch('causes', {
          token: user?.token,
        }),
      useResources: ({ search }) => useCauses({ queries: { search } }),
      extractValueAndName: (r) => {
        return {
          value: r.id,
          name: causeTranslator(r.name),
        }
      },
    }
  },
  rsvpEventRegions: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.rsvpEventRegions'),
      getResource: () =>
        fetch('rsvpEventLocations', {
          token: user?.token,
          queries: { location_type: 'region' },
        }),
      useResources: ({ search }) =>
        useRsvpEventLocations({
          queries: { search, location_type: 'region' },
        }),
      extractValueAndName: (r) => {
        return {
          value: r.name,
          name: r.name,
        }
      },
    }
  },
  rsvpCompanyAdminEventRegions: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.rsvpEventRegions'),
      getResource: () =>
        fetch('rsvpEventLocations', {
          token: user?.token,
          queries: { location_type: 'region', show_cancelled: 'true' },
        }),
      useResources: ({ search }) =>
        useRsvpEventLocations({
          queries: { search, location_type: 'region', show_cancelled: 'true' },
        }),
      extractValueAndName: (r) => {
        return {
          value: r.name,
          name: r.name,
        }
      },
    }
  },
  companyAdminEmployeeCountries: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.companyAdminEmployeeCountries'),
      getResource: () =>
        fetch('companyAdminEmployeeLocations', {
          token: user?.token,
          queries: { location_type: 'country' },
        }),
      useResources: ({ search }) =>
        useCompanyAdminEmployeeLocations({
          queries: { search, location_type: 'country' },
        }),
      extractValueAndName: (r) => {
        return {
          value: r.name,
          name: r.name,
        }
      },
    }
  },
  companyAdminEmployeeRegions: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.companyAdminEmployeeRegions'),
      getResource: () =>
        fetch('companyAdminEmployeeLocations', {
          token: user?.token,
          queries: { location_type: 'region' },
        }),
      useResources: ({ search }) =>
        useCompanyAdminEmployeeLocations({
          queries: { search, location_type: 'region' },
        }),
      extractValueAndName: (r) => {
        return {
          value: r.name,
          name: r.name,
        }
      },
    }
  },
  companyAdminSlackChannels: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.companyAdminSlackChannels'),
      getResource: () =>
        fetch('mySlackChannels', {
          token: user?.token,
        }),
      useResources: ({ search }) => useMySlackChannels({ queries: { search } }, { keepPreviousData: true }),
      extractValueAndName: (r) => {
        return {
          value: r.id,
          name: r.name,
        }
      },
    }
  },
  companyAdminEmployees: () => {
    const user = useContext(UserContext)
    const store = useStore()
    const [modalState, setModalState] = store.use(['modalState'])
    const { t } = useTranslation()

    return {
      placeholder: t('typeaheads.companyAdminEmployees'),
      getResource: () =>
        fetch('companyAdminEmployees', {
          token: user?.token,
        }),
      useResources: ({ search }) =>
        useCompanyAdminEmployees({
          queries: { search, eventIdentifier: modalState?.eventIdentifier },
        }),
      extractValueAndName: (r) => {
        return {
          value: r.user.email,
          name: `${r.user.email} ${
            r.user.firstName
              ? ` (${r.user.firstName}${
                  r.user.lastName ? ` ${r.user.lastName}` : ''
                })`
              : ''
          } `,
        }
      },
    }
  },
  employeeRsvpNonprofit: () => {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    return {
      placeholder: t('typeaheads.adminRsvpNonprofit'),
      getResource: (nonprofitId) =>
        fetch('employeeRsvpNonprofit', {
          args: { nonprofitId },
          token: user?.token,
        }),
      useResources: ({ search }) =>
        useEmployeeRSVPNonprofits({ queries: { search } }),
      extractValueAndName: (r) => {
        return {
          value: r.id,
          name: r.name,
          website: r.websiteLink,
        }
      },
    }
  },
}
